import api from './api';
import { dedupeInflight } from '../utils/dedupedFetch';

function roomAvailabilityKey({ stayId, roomId, checkIn, checkOut, quantity = 1 }) {
  return `inv:${stayId}:${roomId}:${checkIn}:${checkOut}:${quantity}`;
}

/**
 * Check one room's inventory for a stay date range.
 */
export async function checkRoomAvailability(params) {
  const key = roomAvailabilityKey(params);
  return dedupeInflight(
    key,
    async () => {
      const response = await api.post('/api/inventory/check-availability', {
        stayId: params.stayId,
        roomId: params.roomId,
        checkIn: params.checkIn,
        checkOut: params.checkOut,
        quantity: params.quantity ?? 1
      });
      return response.data;
    },
    { cacheMs: 8000 }
  );
}

/**
 * True if at least one room can accommodate the request for every night in range.
 */
export async function checkStayHasAvailability({
  stayId,
  rooms,
  checkIn,
  checkOut,
  quantity = 1
}) {
  const list = Array.isArray(rooms) ? rooms : [];
  if (!stayId || !checkIn || !checkOut || !list.length) {
    return { canAccommodate: false, roomResults: [] };
  }

  const roomIds = list
    .map((room) => room.roomId || room.id)
    .filter(Boolean)
    .sort()
    .join(',');

  return dedupeInflight(
    `stay-avail:${stayId}:${checkIn}:${checkOut}:${quantity}:${roomIds}`,
    async () => {
      const roomResults = await Promise.all(
        list.map(async (room) => {
          const roomId = room.roomId || room.id;
          if (!roomId) return { roomId: null, canAccommodate: false };
          try {
            const data = await checkRoomAvailability({
              stayId,
              roomId,
              checkIn,
              checkOut,
              quantity
            });
            return { roomId, canAccommodate: data.canAccommodate === true };
          } catch {
            return { roomId, canAccommodate: false };
          }
        })
      );

      return {
        canAccommodate: roomResults.some((r) => r.canAccommodate),
        roomResults
      };
    },
    { cacheMs: 8000 }
  );
}

export default { checkRoomAvailability, checkStayHasAvailability };
